import { Authority } from '../types/user.type';
import { IAuthUser, IJWToken } from './types/auth.interface';

type AuthoritySubject = Pick<IAuthUser, 'authority'> | Pick<IJWToken, 'auth'>;
type AuthorityResellerSubject = (
  Pick<IAuthUser, 'authority' | 'reseller'>
  | Pick<IJWToken, 'auth' | 'reseller'>
);

const adminProjectsAuthorities: readonly Authority[] = ['support', 'sales', 'superadmin'];
const batteryCatalogAdminAuthorities: readonly Authority[] = ['support', 'sales', 'superadmin'];
const fullAdminOrgAuthorities: readonly Authority[] = ['sales', 'superadmin'];
const advanceOrderStatusAuthorities: readonly Authority[] = ['sales', 'support', 'superadmin'];
const poSalesApprovalAuthorities: readonly Authority[] = ['sales', 'superadmin'];
const poSupportApprovalAuthorities: readonly Authority[] = ['support', 'superadmin'];
const poRejectionAuthorities: readonly Authority[] = ['support', 'sales', 'superadmin'];
const customerProfilesAuthorities: readonly Authority[] = ['admin', 'sales', 'superadmin'];
const eventsAuthorities: readonly Authority[] = ['admin', 'sales', 'superadmin'];
const defaultAdminResellerKey = 'EMSYS';

function getAuthority(subject: AuthoritySubject | null): Authority | null {
  if (subject == null) {
    return null;
  }

  return 'authority' in subject ? subject.authority : subject.auth;
}

function getReseller(subject: AuthorityResellerSubject | null): string | null {
  if (subject == null) {
    return null;
  }

  return subject.reseller;
}

function hasAuthority(
  subject: AuthoritySubject | null,
  authorities: readonly Authority[],
): boolean {
  const authority = getAuthority(subject);

  return authority != null && authorities.includes(authority);
}

export function isSuperAdmin(subject: AuthoritySubject | null): boolean {
  return getAuthority(subject) === 'superadmin';
}

export function hasAdminProjectsAccess(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, adminProjectsAuthorities) && getReseller(subject) === adminResellerKey;
}

export function hasFullAdminOrgAccess(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, fullAdminOrgAuthorities) && getReseller(subject) === adminResellerKey;
}

export function canAccessBatteryCatalogAdmin(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, batteryCatalogAdminAuthorities) && getReseller(subject) === adminResellerKey;
}

export function isInAdminOrg(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return getReseller(subject) === adminResellerKey;
}

export function canAdvanceOrderStatus(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, advanceOrderStatusAuthorities) && getReseller(subject) === adminResellerKey;
}

export function canSubmitProjectPo(
  subject: AuthorityResellerSubject | null,
  projectReseller: string,
  adminResellerKey = defaultAdminResellerKey,
): boolean {
  const authority = getAuthority(subject);
  const actorReseller = getReseller(subject);

  if (authority == null || actorReseller == null || authority === 'support') {
    return false;
  }

  if (
    actorReseller === adminResellerKey
    && fullAdminOrgAuthorities.includes(authority)
  ) {
    return projectReseller === adminResellerKey;
  }

  if (actorReseller !== projectReseller) {
    return false;
  }

  return canAccessResellerWorkspace(subject);
}

export function canApprovePoSales(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, poSalesApprovalAuthorities) && getReseller(subject) === adminResellerKey;
}

export function canApprovePoSupport(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, poSupportApprovalAuthorities) && getReseller(subject) === adminResellerKey;
}

export function canRejectPo(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, poRejectionAuthorities) && getReseller(subject) === adminResellerKey;
}

export function hasCustomerProfilesAdminAccess(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, customerProfilesAuthorities) && getReseller(subject) === adminResellerKey;
}

export const canManageUsers = (subject: AuthoritySubject | null): boolean =>
  isSuperAdmin(subject) || getAuthority(subject) === 'admin';

export function isResellerAdmin(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  const reseller = getReseller(subject);

  return getAuthority(subject) === 'admin'
    && reseller != null
    && reseller !== adminResellerKey;
}

export function canAccessResellerWorkspace(subject: AuthoritySubject | null): boolean {
  const authority = getAuthority(subject);

  return authority != null && authority !== 'support';
}

export function canViewResellerEvents(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, eventsAuthorities) && getReseller(subject) !== adminResellerKey;
}

export function canViewAdminOrgEvents(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return hasAuthority(subject, eventsAuthorities) && getReseller(subject) === adminResellerKey;
}

export function canViewEvents(
  subject: AuthorityResellerSubject | null,
  adminResellerKey: string,
): boolean {
  return canViewResellerEvents(subject, adminResellerKey) || canViewAdminOrgEvents(subject, adminResellerKey);
}

export function canAssignAdminOrgAuthority(
  subject: Pick<IJWToken, 'auth'> | null,
  targetReseller: string | null,
  adminResellerKey: string,
): boolean {
  return isSuperAdmin(subject) && targetReseller === adminResellerKey;
}
